<template>
    <div class="relative">
        <draggable :list="estimate.preliminaries" draggable=".draggable" handle=".handle"
                   @change="estimate.update(); estimate.addSnapshot(); resizeElements();">
            <estimate-preliminary-group v-for="(group,index) in estimate.preliminaries" :key="index" ref="groups"
                                        :group="group"
                                        :preliminaries="preliminaries"
                                        :estimate="estimate"
                                        :multimode="multimode"
                                        @ping="(e) => $emit('ping',e)"
                                        :clipboard-key="clipboardKey"
                                        @reflow="resizeElements"
                                        :clipboard-group-item-data="clipboardGroupItemData"
                                        :clipboard-group-data="clipboardGroupData"
                                        :pinging="pinging"
                                        :store="store"
            ></estimate-preliminary-group>
        </draggable>

        <!-- Total. -->
        <div class="flex text-xs text-gray-900 text-right font-bold">
            <div class="flex-1 px-2 py-1">Preliminaries Total</div>
            <div class="w-28 -ml-px px-2 py-1">
                {{
                    (preliminaries.reduce((a, group) => a + group.items.reduce((a, item) => a + (item.total ? item.total : 0), 0), 0)) | toCurrency
                }}
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>

        <!-- New Group. -->
        <div class="flex items-center mt-4 mb-1" v-if="!estimate.readonly">
            <div class="w-6 flex-shrink-0">
                <i class="fa fa-plus text-sm text-gray-400 pl-1"></i>
            </div>
            <div class="w-16">
                <page-cell
                    class="text-sm -my-px px-2 py-1.5 border-white font-bold text-gray-900 bg-white"
                    :disabled="estimate.readonly"
                    :placeholder="nextGroupReference()"
                    v-model="newGroup.reference"
                    @input="addGroup('reference')"
                ></page-cell>
            </div>
            <div class="flex-1">
                <input
                    placeholder="Add a new preliminary group..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border-white font-bold text-gray-900 shadow-none relative hover:z-10 focus:z-20 resize-none bg-white"
                    type="text"
                    :disabled="estimate.readonly"
                    v-model="newGroup.name"
                    @input="addGroup('name')"
                >
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1">
                <ui-dropdown menu-class="p-3 w-56" :key="clipboardKey"
                             v-if="!estimate.readonly && (clipboardGroupData[0] || clipboardGroupData.length > 1)">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardGroupData[0])"
                                          @click="pasteGroup(clipboardGroupData[0],false);close();">
                            Paste Group
                        </ui-dropdown-item>
                        <ui-dropdown-divider
                            v-if="!estimate.readonly && (clipboardGroupData.length > 1)"></ui-dropdown-divider>
                        <ui-dropdown-item :ref="'paste'+(3)+'item'"
                                          @mouseleave="closePasteSubMenu(3)"
                                          @mouseenter="openPasteSubMenu(3)"
                                          v-if="!estimate.readonly && (clipboardGroupData.length > 1)"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste'+(3)+'relative'">
                                <vue-context :ref="'paste'+(3)+'menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste'+(3)+'menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(item,i) in clipboardGroupData"
                                                          :key="'paste'+(3)+'-'+i"
                                                          v-if="i > 0"
                                                          @click="pasteGroup(item.data,false);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ item.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>

            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {Estimate} from "../../../models";
import RoutePlansJobItem from "../../logistics/route-plans/RoutePlansJobItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import {cloneDeep} from "lodash";
import remoteStorage from "../../../utils/remoteStorage";
import * as math from "mathjs";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import EstimatePreliminaryGroup from "./EstimatePreliminaryGroup.vue";
import PageCell from "../../ui/PageCell.vue";
import UiDropdown from "../../ui/UiDropdown.vue";

export default {
    components: {
        UiDropdown,
        PageCell,
        EstimatePreliminaryGroup,
        UiDropdownDivider,
        UiDropdownItem,
        RoutePlansJobItem,
        draggable,
    },
    props: {
        estimate: Estimate,
        user: Object,
        store: Object,
        multimode: false,
        pinging: null,
        clipboardKey: Number,
        clipboardGroupData: Array,
        clipboardGroupItemData: Array,
    },
    data() {
        return {
            newGroup: {
                reference: null,
                name: null,
            },
            hasNonDefaultMarkup: false,
            disableScroller: false,
            scrollPosition: false,
            originalScrollPosition: false,
            scrollTimer: false,
            preliminaries: this.estimate.preliminaries
        }
    },
    watch: {
        estimate: {
            handler(newValue, oldValue) {
                this.preliminaries = this.estimate.preliminaries
                this.resizeElements()
            },
        },
    },
    computed: {},
    methods: {
        resizeElements(forceRedraw) {
            this.preliminaries = this.estimate.preliminaries
            this.$nextTick(() => {
                if (this.$refs['groups']) {
                    this.$refs['groups'].forEach((group) => {
                        if (group.$refs['items']) {
                            group.$refs['items'].forEach((item) => {
                                if (item.$refs['autoresizable-description']) {
                                    item.$refs['autoresizable-description'].resizeTextarea({target: item.$refs['autoresizable-description'].$el})
                                }
                            })
                        }
                    })
                }
                this.setIsInView()
            })
        },
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        addGroup(trigger) {
            this.estimate.preliminaries.push({
                reference: this.newGroup.reference ?? this.nextGroupReference(),
                defaultReference: this.nextGroupReference(),
                name: this.newGroup.name,
                items: [],
                color: '#FFFFFF'
            });
            this.$nextTick(() => {
                if (trigger === 'name') {
                    this.$refs.groups[this.$refs.groups.length - 1].$refs.name.focus()
                } else {
                    this.$refs.groups[this.$refs.groups.length - 1].$refs.reference.disableSelectAll()
                    this.$refs.groups[this.$refs.groups.length - 1].$refs.reference.$el.focus()
                    this.$refs.groups[this.$refs.groups.length - 1].$refs.reference.enableSelectAll()
                }
                this.newGroup.reference = null
                this.newGroup.name = ''
                this.estimate.update()
            })
        },
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteGroup(group, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'group');
            if (at) {
                let num = this.preliminaries.indexOf(at);
                this.preliminaries.splice(num, 0, cloneDeep(group));
            } else {
                this.preliminaries.push(cloneDeep(group));
            }
            this.estimate.update();
            this.estimate.addSnapshot();
        },
        nextGroupReference() {
            const groupNumber = this.preliminaries.length + 1
            return `${groupNumber}`
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        setIsInView() {
            let currentScroll = this.scrollPosition;
            let currentTop = document.getElementById('ui-crud-main-body').getBoundingClientRect().top + currentScroll
            let currentBottom = currentTop + document.getElementById('ui-crud-main-body').getBoundingClientRect().height;
            let added = 0;
            let removed = 0;
            if (this.$refs['groups']) {
                this.$refs['groups'].forEach((group) => {
                    if (group.$refs['items']) {
                        group.$refs['items'].forEach((item) => {
                            let p = item.$el.getBoundingClientRect();
                            this.distance = {top: p.top + currentScroll, bottom: p.bottom + currentScroll}
                            let inView = (this.distance.top < (currentBottom + 2)) && (this.distance.bottom > (currentTop + 2));
                            item.setIsInView(inView)
                        })
                    }
                })
            }
        },
        scroller() {
            this.scrollPosition = document.getElementById('ui-crud-main-body').scrollTop;
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(() => {
                if (!this.disableScroller) {
                    this.disableScroller = true;
                    this.$nextTick(() => {
                        this.setIsInView()
                        this.$nextTick(() => {
                            document.getElementById('ui-crud-main-body').scrollTo({top: this.scrollPosition})
                            this.disableScroller = false;
                        })
                    })
                }
            }, 50)
        },
    },
    mounted() {
        window.addEventListener('scroll', this.scroller, true);
        this.$nextTick(() => {
            this.setIsInView()
        })
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.scroller, true)
    },
}
</script>
